<script>
/**
 * Mixin for filter and sorting options of DMP stats
 */
// Vuex
import { mapState } from 'vuex';
import { GET_PB_TYPES, GET_ZONES } from '@/store/mutation-types';
import { FETCH_PB_TYPES, FETCH_ZONES } from '@/store/action-types';
// Utils
import {
  MEETING_RATING_SCALE,
  STAT_CATEGORIES,
  STAT_CATEGORIES_PROBLEMS,
  STAT_TYPES,
} from '@/utils/constants';
import { valueKeyFormat, ChartHelper, formatConfig } from '@/utils/indicator-utils';

export default {

  data() {
    return {
      chartHelper: null,
      statsHolderDestroyed: false,
      /** Array: Categories used in the list of checkboxes */
      categoryItems: STAT_CATEGORIES.map((cat, idx) => ({ id: idx, label: this.$t(cat.i18nLabel) })),
    };
  },

  computed: {
    statsOptions() {
      // Filters and sorting
      let options = [
        {
          key: 'statsCategoriesLegend',
          component: 'div',
          options: {
            class: 'legend mt10 mb10',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.CATEGORIES'),
          }],
        },
        {
          key: 'selectedCategories',
          component: 'CheckboxList',
          value: this.statsOptSelectedCategories,
          options: {
            items: this.categoryItems,
          },
        },
      ];
      if (this.statsOptDisplayProblemsOptions) {
        options = [
          ...options,
          {
            key: 'statsTypesFilterSeparator',
            value: '',
            component: 'Div',
            options: { class: 'filters-zone__modal__sep' },
          },
          {
            key: 'statsTypesFilterLegend',
            value: '',
            component: 'Div',
            options: { class: 'legend legend--bottom-margin' },
            slots: [{
              component: 'Div',
              content: this.$t('message.TYPES'),
            }],
          },
          {
            key: 'selectedTypesInclusive',
            value: this.statsOptSelectedPbTypesInclusive,
            component: 'IntCheckbox',
            options: {
              class: 'block',
              title: this.$t('message.TYPE_INCLUSIVE'),
            },
          },
          {
            key: 'selectedTypes',
            component: 'Tree',
            value: this.statsOptSelectedProblemsTypes,
            options: {
              ref: 'typesTree',
              multiple: true,
              items: this.statsOptAllTypes,
              childrenKey: 'types',
              nameKey: 'name',
            },
          },
          {
            key: 'statsZonesFilterSeparator',
            value: '',
            component: 'Div',
            options: { class: 'filters-zone__modal__sep' },
          },
          {
            key: 'statsZoneFilterLegend',
            value: '',
            component: 'Div',
            options: { class: 'legend legend--bottom-margin' },
            slots: [{
              component: 'Div',
              content: this.$t('message.LOCATIONS'),
            }],
          },
          {
            key: 'statsSelectedZonesInclusive',
            value: this.statsOptSelectedZonesInclusive,
            component: 'IntCheckbox',
            options: {
              class: 'block',
              title: this.$t('message.ZONE_INCLUSIVE'),
            },
          },
          {
            key: 'statsSelectedZones',
            component: 'Tree',
            value: this.statsOptSelectedZones,
            options: {
              ref: 'zonesTree',
              multiple: true,
              items: this.statsOptAllZones,
              childrenKey: 'zones',
              nameKey: 'name',
            },
          },
        ];
      }
      // Display options
      options = [
        ...options,
        {
          key: 'options-title',
          value: '',
          component: 'h2',
          options: {
            class: 'basic-sub-title',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.DISPLAY_NOUN'),
          }],
        },
        {
          key: 'statsOptShowLabels',
          value: this.statsOptShowLabels,
          component: 'Checkbox',
          options: {
            class: 'block',
            title: this.$t('message.INDIC_SETTINGS_DISPLAY_LABELS'),
          },
        },
        {
          key: 'statsOptShowValues',
          value: this.statsOptShowValues,
          component: 'Checkbox',
          options: {
            class: 'block',
            title: this.$t('message.INDIC_SETTINGS_DISPLAY_VALUES'),
          },
        },
      ];
      return options;
    },
    /** Array: IDs of selected categories */
    statsOptSelectedCategories() {
      if (this.allPageOptions && this.allPageOptions.selectedCategories) {
        return this.allPageOptions.selectedCategories;
      }
      return STAT_CATEGORIES.map(cat => cat.id);
    },
    /** Array: Return the ids of types that should be selected */
    statsOptSelectedProblemsTypes() {
      if (this.allPageOptions && this.allPageOptions.selectedTypes) {
        return this.allPageOptions.selectedTypes;
      }
      return [];
    },
    /** Boolean: Return true if you want to display childrens types of a problem type */
    statsOptSelectedPbTypesInclusive() {
      if (this.allPageOptions && !isNaN(this.allPageOptions.selectedTypesInclusive)) {
        return this.allPageOptions.selectedTypesInclusive;
      }
      return false;
    },
    /** Array: Types used in the list of checkboxes */
    statsOptAllTypes() {
      return [{ id: 0, name: this.$t('message.UNSPECIFIED'), selected: false }].concat(this.pbOptTypes);
    },
    /** Array: Return the ids of zones that should be selected */
    statsOptSelectedZones() {
      if (this.allPageOptions && this.allPageOptions.statsSelectedZones) {
        return this.allPageOptions.statsSelectedZones;
      }
      return [];
    },
    /** Boolean: Return true if you want to display childrens types of a problem type */
    statsOptSelectedZonesInclusive() {
      if (this.allPageOptions && !isNaN(this.allPageOptions.statsSelectedZonesInclusive)) {
        return this.allPageOptions.statsSelectedZonesInclusive;
      }
      return false;
    },
    /** Array: Types used in the list of checkboxes */
    statsOptAllZones() {
      return [{ id: 0, name: this.$t('message.UNSPECIFIED'), selected: false }].concat(this.statsOptZones);
    },
    /** If defined, contains all the options above */
    allPageOptions() {
      return null;
    },
    /** If problem category is selected return true */
    statsOptDisplayProblemsOptions() {
      return this.statsOptSelectedCategories.includes(STAT_CATEGORIES_PROBLEMS);
    },
    /** Boolean: Whether to display labels on the charts */
    statsOptShowLabels() {
      if (this.allPageOptions && !isNaN(this.allPageOptions.statsOptShowLabels)) {
        return this.allPageOptions.statsOptShowLabels;
      }
      return false;
    },
    /** Boolean: Whether to display values on the charts */
    statsOptShowValues() {
      if (this.allPageOptions && !isNaN(this.allPageOptions.statsOptShowValues)) {
        return this.allPageOptions.statsOptShowValues;
      }
      return false;
    },
    ...mapState({
      pbOptTypes: state => state[GET_PB_TYPES.stateKey] || [],
      pbOptTypesLoading: state => state[GET_PB_TYPES.loadingKey],
      statsOptZones: state => state[GET_ZONES.stateKey] || [],
      statsOptZonesLoading: state => state[GET_ZONES.loadingKey],
    }),
  },

  created() {
    this.chartHelper = new ChartHelper();
    if (this.currentTeam && this.currentTeam.id) {
      // Fetch all types
      if (!this.pbOptTypesLoading) {
        this.$store.dispatch(FETCH_PB_TYPES);
      }
      // Fetch all zones
      if (!this.statsOptZonesLoading) {
        this.$store.dispatch(FETCH_ZONES);
      }
    }
  },

  beforeDestroy() {
    this.statsHolderDestroyed = true;
  },

  methods: {
    /**
     * Helper ; Format data fetched from getStats.php
     * @param {Object} rawData - Date fetched from getStats.php
     * @return {Object} Formatted data
     */
    statsFormatStatsData(rawData, displayConfig) {
      if (this.statsHolderDestroyed || !rawData.days) {
        // Component destroyed
        return {};
      }
      const {
        days,
        pbTypes,
        pbIndics,
        pbRecurrence,
        ratings,
      } = rawData;
      const statsData = {};
      days.forEach((dayValue) => {
        dayValue.momentDate = this.$moment(dayValue.fullDate);
        dayValue.valueKey = dayValue.momentDate.format(valueKeyFormat);
      });

      // Create an array of daily values using the ChartHelper
      STAT_TYPES.forEach((stat) => {
        if (stat.chartType === 'line' && days.length > 0 && days[0][stat.key] !== undefined) {
          statsData[stat.key] = this.statsFormatDailyStat(stat, days, displayConfig);
        }
      });

      // Special values ; results depend on range given
      statsData.pbTypes = pbTypes.map(d => ({ label: d.name, value: d.nb }));
      statsData.pbIndics = pbIndics.map(d => ({ label: d.name, value: d.nb }));
      statsData.pbRecurrence = pbRecurrence.map(d => ({ label: d.name, value: d.nb }));
      statsData.ratings = ratings.map((d, idx) => (
        { label: this.$t(MEETING_RATING_SCALE[idx].i18nLabel), value: d.nb }
      ));
      return statsData;
    },
    /**
     * Create an array of daily values for a specific stat using the raw data from the server.
     * @param  {Object} stat            Stat to build the data
     * @param  {Array} dayValues        Raw data
     * @param  {Object} displayConfig   Same as indicators' display configs
     * @return {Array}                  The array of data/labels to pass to the stat-chart component
     */
    statsFormatDailyStat(stat, dayValues, displayConfig) {
      const values = dayValues.reduce((all, val) => ({
        ...all,
        [val.valueKey]: {
          reported: val.valueKey,
          value: val[stat.key],
        },
      }), {});

      const { chartHelper: ch, range } = this;
      const { workingDays } = this.currentTeam;
      const formattedConfig = formatConfig(displayConfig);
      const fakeIndic = { workingDays, isAverage: ['ongoingProblems', 'ongoingActions'].includes(stat.key) ? 1 : 0 };
      const { points } = ch.buildCustomConfigChart(fakeIndic, values, formattedConfig, workingDays, range);

      return points.map(p => ({ label: p.tooltip.title, value: p.data }));
    },
    /**
     * Create a string formatted as query params with statsOptions params
     * @param {Object} options - user custom options
     * @return {String} Query
     */
    statsOptQueryParams(options) {
      if (!options) {
        return '';
      }
      let query = '';
      let amp = '';
      if (options.selectedCategories) {
        query += `selectedCategories=${options.selectedCategories.join(',')}`;
        amp = '&';
      }
      if (options.selectedTypes && options.selectedTypes.length) {
        const selectedTypesQuery = `${amp}selectedTypes=${options.selectedTypes.join(',')}`;
        const selectedTypesInclusiveQuery = `&selectedTypesInclusive=${!!options.selectedTypesInclusive}`;
        query += selectedTypesQuery + selectedTypesInclusiveQuery;
        amp = '&';
      }
      if (options.statsSelectedZones && options.statsSelectedZones.length) {
        const selectedZonesQuery = `${amp}statsSelectedZones=${options.statsOptSelectedZones.join(',')}`;
        const selectedZonesIncQuery = `&statsSelectedZonesInclusive=${!!options.statsOptSelectedZonesInclusive}`;
        query += selectedZonesQuery + selectedZonesIncQuery;
        amp = '&';
      }
      if (options.statsOptShowLabels) {
        query += '&statsOptShowLabels=true';
        amp = '&';
      }
      if (options.statsOptShowValues) {
        query += '&statsOptShowValues=true';
        amp = '&';
      }
      return query;
    },
  },
};
</script>
